import { useCallback, useEffect, useState } from "react";
import { IonModal, IonSpinner, useIonToast } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import config from "../../settings/config";
import { getAuthToken } from "../../services/authStorage";
import "./WatchOnOtherDeviceSheet.css";

/**
 * "Guarda su un altro dispositivo" (client, 29/09/2026): a personal link, valid
 * 4 hours, that opens this movement video in the browser of a PC, Mac, tablet
 * or Smart TV without logging in — plus a short code for typing on a TV at
 * dietasocial.it/tv.
 *
 * A fresh link is created each time the sheet opens; "Disattiva link" switches
 * it off straight away.
 */
const expiryLabel = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  });
};

// Loaded on tap: the player's tests mock @capacitor/core without plugins.
const isShareCanceledError = (error) =>
  /cancel|abort/i.test(String(error?.message || error?.name || ""));

// Not redux/actions: this sheet lives inside the player, whose tests must not
// pull in the whole API layer.
const apiBase = () => config?.env?.[config?.env?.mode]?.url;
const headersDefault = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
});

const readError = async (response) => {
  try {
    const body = await response.json();
    return body?.message || null;
  } catch (e) {
    return null;
  }
};

export default function WatchOnOtherDeviceSheet({ isOpen, onClose, vimeoId, context = null }) {
  const [presentToast] = useIonToast();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const create = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLink(null);
    try {
      const response = await fetch(`${apiBase()}/api/v1/watch-links`, {
        method: "POST",
        headers: headersDefault(),
        body: JSON.stringify({ vimeo_id: String(vimeoId), context }),
      });
      if (!response.ok) {
        setError(
          (await readError(response)) ||
            (response.status === 429
              ? "Hai creato troppi link. Riprova tra un minuto."
              : "Non è stato possibile creare il link. Riprova."),
        );
        return;
      }
      setLink(await response.json());
    } catch (e) {
      setError("Connessione assente. Controlla internet e riprova.");
    } finally {
      setLoading(false);
    }
  }, [vimeoId, context]);

  useEffect(() => {
    if (isOpen && vimeoId) create();
    if (!isOpen) {
      setLink(null);
      setError(null);
    }
    // Only when the sheet opens: a new link per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vimeoId]);

  const copy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      presentToast({ message: "Link copiato", duration: 1800 });
    } catch (e) {
      presentToast({ message: "Copia non riuscita: tieni premuto il link per copiarlo.", duration: 2500 });
    }
  };

  const share = async () => {
    if (!link?.url) return;
    const title = "Guarda il video su un altro dispositivo";
    const text = "Apri questo link sul computer o sulla TV (valido fino alle " + expiryLabel(link.expires_at) + ")";
    try {
      if (Capacitor.isNativePlatform()) {
        const { Share } = await import("@capacitor/share");
        await Share.share({ title, text, url: link.url, dialogTitle: "Condividi link" });
      } else if (navigator.share) {
        await navigator.share({ title, text, url: link.url });
      } else {
        await copy();
      }
    } catch (e) {
      if (!isShareCanceledError(e)) {
        presentToast({ message: "Condivisione non riuscita. Usa Copia link.", duration: 2500 });
      }
    }
  };

  const revoke = async () => {
    if (!link?.id) return;
    try {
      await fetch(`${apiBase()}/api/v1/watch-links/${link.id}`, {
        method: "DELETE",
        headers: headersDefault(),
      });
      presentToast({ message: "Link disattivato", duration: 1800 });
    } catch (e) {
      presentToast({ message: "Non è stato possibile disattivare il link.", duration: 2500 });
      return;
    }
    onClose?.();
  };

  const tvHost = (link?.tv_url || "dietasocial.it/tv").replace(/^https?:\/\//, "");

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={0.75}
      breakpoints={[0, 0.75, 1]}
      className="wod-sheet"
    >
      <div className="wod">
        <h2 className="wod__title">Guarda su un altro dispositivo</h2>
        <p className="wod__lead">
          Apri il video sul computer, sul tablet o sulla Smart TV, senza accedere.
        </p>

        {loading ? (
          <div className="wod__loading">
            <IonSpinner name="crescent" />
          </div>
        ) : null}

        {error ? (
          <div className="wod__error">
            <p>{error}</p>
            <button type="button" className="wod__btn wod__btn--primary" onClick={create}>
              Riprova
            </button>
          </div>
        ) : null}

        {link ? (
          <>
            <section className="wod__block">
              <h3>Sulla TV</h3>
              <p>
                Apri <strong>{tvHost}</strong> nel browser della TV e inserisci il codice:
              </p>
              <div className="wod__code" aria-label={`Codice ${link.code}`}>
                {link.code}
              </div>
            </section>

            <section className="wod__block">
              <h3>Sul computer o sul tablet</h3>
              <div className="wod__url" data-testid="wod-url">
                {link.url}
              </div>
              <div className="wod__actions">
                <button type="button" className="wod__btn wod__btn--primary" onClick={copy}>
                  Copia link
                </button>
                <button type="button" className="wod__btn" onClick={share}>
                  Condividi
                </button>
              </div>
            </section>

            <p className="wod__expiry">
              Link e codice personali, validi fino alle {expiryLabel(link.expires_at)}.
            </p>
            <button type="button" className="wod__revoke" onClick={revoke}>
              Disattiva link
            </button>
          </>
        ) : null}
      </div>
    </IonModal>
  );
}
